<template>
  <section class="support-tickets-page">
    <!-- Ticket Preview Sidebar -->
    <TicketPreviewSidebar
      v-model="showPreviewSidebar"
      :ticket-id="selectedTicketId"
      @closed="onPreviewClosed"
      @navigate-to-details="navigateToDetails"
    />

    <!-- Filter Sidebar -->
    <b-sidebar
      v-model="showFilters"
      right
      shadow
      backdrop
      bg-variant="white"
      no-header
    >
      <template>
        <div class="container sidebar-header">
          <b-row>
            <div class="col">
              <b-button
                variant="primary"
                @click="applyFilters()"
              >Apply</b-button>
            </div>
            <div class="col-auto text-right">
              <b-button
                type="reset"
                variant="outline-danger"
                @click="resetFormFilters()"
              >Reset</b-button>
              <b-button
                variant="outline-secondary"
                class="ml-1"
                @click="cancelFilters()"
              >Cancel</b-button>
            </div>
          </b-row>
        </div>

        <div class="px-3 py-2">
          <b-form-group label="Search:">
            <b-form-input v-model="formFilters.search" placeholder="Search tickets..." />
          </b-form-group>
          <b-form-group label="Status:">
            <b-v-select
              v-model="formFilters.status"
              :options="statusList"
            />
          </b-form-group>
          <b-form-group label="Priority:">
            <b-v-select
              v-model="formFilters.priority"
              :options="priorityFilter"
            />
          </b-form-group>
          <b-form-group label="Type:">
            <b-v-select
              v-model="formFilters.type"
              :options="typeList"
            />
          </b-form-group>
          <b-form-group label="Ticket By:" v-if="is('superadmin')">
            <b-v-select
              v-model="formFilters.userId"
              :searchable="true"
              :options="posBy"
            />
          </b-form-group>
        </div>
      </template>
    </b-sidebar>

    <!-- Page Header -->
    <div class="header">
      <div class="header-body">
        <div class="row align-items-end">
          <div class="col">
            <h1 class="header-title">Support Tickets</h1>
            <ActiveFiltersCard
              :itemsFilters="itemsFilters"
              :resetFilterWithKey="resetFilterWithKey"
              :loadItems="loadTickets"
            />
          </div>
          <div class="col-auto">
            <router-link
              :to="{ name: 'Support.NewTickets.Create' }"
              class="btn btn-success mr-2"
            >
              <i class="mr-2 fa fa-plus" />
              New Ticket
            </router-link>
            <b-button variant="primary" @click="showFilters = !showFilters">
              <i class="fa fa-filter mr-2"></i>
              Filters
            </b-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs for Active/Completed -->
    <b-card no-body>
      <b-tabs card v-model="activeTab" @input="onTabChange" nav-class="bg-light">
        <b-tab no-body title="Active Tickets" title-link-class="font-weight-bold">
          <TicketListTable
            :items="activeTickets"
            :loading="loadingActive"
            :pagination="activePagination"
            :failed="failedActive"
            @row-clicked="openPreview"
            @pagination-changed="loadActiveTickets"
            @priority-changed="onPriorityChanged"
          />
        </b-tab>
        <b-tab no-body title="Closed Tickets" title-link-class="font-weight-bold">
          <TicketListTable
            :items="completedTickets"
            :loading="loadingCompleted"
            :pagination="completedPagination"
            :failed="failedCompleted"
            show-closed-date
            @row-clicked="openPreview"
            @pagination-changed="loadCompletedTickets"
            @priority-changed="onPriorityChanged"
          />
        </b-tab>
      </b-tabs>
    </b-card>
  </section>
</template>

<script>
import ActiveFiltersCard from '@/views/components/ActiveFiltersCard.vue';
import TicketListTable from './TicketListTable.vue';
import TicketPreviewSidebar from './TicketPreviewSidebar.vue';
import { myCommonDataMixin } from '@/helpers/utils/vueMixins.js';

export default {
  name: 'NewTicketsIndex',
  mixins: [myCommonDataMixin],
  components: {
    ActiveFiltersCard,
    TicketListTable,
    TicketPreviewSidebar
  },

  data: () => ({
    activeTab: 0,
    showFilters: false,
    showPreviewSidebar: false,
    selectedTicketId: null,

    // Active tickets
    activeTickets: [],
    loadingActive: false,
    failedActive: false,
    activePagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 15
    },

    // Completed tickets
    completedTickets: [],
    loadingCompleted: false,
    failedCompleted: false,
    completedPagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      perPage: 15
    },

    // Filters
    itemsFilters: {
      search: '',
      status: '',
      priority: -1,
      type: '',
      userId: ''
    },
    formFilters: {
      search: '',
      status: '',
      priority: -1,
      type: '',
      userId: ''
    },

    // Filter options
    statusList: [
      { value: '', text: 'All' },
      { value: 'triage', text: 'Triage' },
      { value: 'backlog', text: 'Draft' },
      { value: 'unstarted', text: 'Open' },
      { value: 'QA', text: 'QA' },
      { value: 'On Hold', text: 'On Hold' },
      { value: 'started', text: 'In Progress' }
    ],
    priorityFilter: [
      { value: -1, text: 'All' },
      { value: 1, text: 'Low' },
      { value: 2, text: 'Medium' },
      { value: 3, text: 'High' },
      { value: 4, text: 'Urgent' }
    ],
    typeList: [
      { value: '', text: 'All' },
      { value: 'Bug Report', text: 'Bug Report' },
      { value: 'New Feature Suggestion', text: 'New Feature' },
      { value: 'Feature Improvement', text: 'Improvement' },
      { value: 'General Support', text: 'General Support' }
    ],
    usersAll: []
  }),

  computed: {
    posBy() {
      let arrs = [{ value: '', text: 'Any User' }];
      this.usersAll.forEach(itm => {
        arrs.push({
          value: itm.id,
          text: itm.fullName + ' (' + itm.email + ')'
        });
      });
      return arrs;
    }
  },

  mounted() {
    this.loadUsers();
    this.loadActiveTickets();
  },

  methods: {
    loadUsers() {
      this.$http
        .get('/api/userspublic')
        .then(resp => {
          this.usersAll = resp.data;
        })
        .catch(err => {
          console.error('Failed to load users', err);
        });
    },

    loadTickets() {
      if (this.activeTab === 0) {
        this.loadActiveTickets();
      } else {
        this.loadCompletedTickets();
      }
    },

    loadActiveTickets() {
      this.loadingActive = true;
      this.failedActive = false;

      var params = {
        ...this.itemsFilters,
        showOpen: 1,
        page: this.activePagination.currentPage,
        perPage: this.activePagination.perPage
      };

      this.$http
        .post('/api/common/tickets/listItems', params)
        .then(resp => {
          this.activeTickets = resp.data.items || [];
          this.activePagination.totalItems = resp.data.total || 0;
          this.activePagination.totalPages = resp.data.totalPages || 1;
        })
        .catch(err => {
          console.error('Failed to load active tickets', err);
          this.failedActive = true;
        })
        .finally(() => {
          this.loadingActive = false;
        });
    },

    loadCompletedTickets() {
      this.loadingCompleted = true;
      this.failedCompleted = false;

      var params = {
        ...this.itemsFilters,
        status: 'completed',
        showOpen: 0,
        page: this.completedPagination.currentPage,
        perPage: this.completedPagination.perPage
      };

      this.$http
        .post('/api/common/tickets/listItems', params)
        .then(resp => {
          this.completedTickets = resp.data.items || [];
          this.completedPagination.totalItems = resp.data.total || 0;
          this.completedPagination.totalPages = resp.data.totalPages || 1;
        })
        .catch(err => {
          console.error('Failed to load completed tickets', err);
          this.failedCompleted = true;
        })
        .finally(() => {
          this.loadingCompleted = false;
        });
    },

    onTabChange(tabIndex) {
      if (tabIndex === 0) {
        this.loadActiveTickets();
      } else if (tabIndex === 1) {
        this.loadCompletedTickets();
      }
    },

    applyFilters() {
      this.itemsFilters = { ...this.formFilters };
      this.activePagination.currentPage = 1;
      this.completedPagination.currentPage = 1;
      this.showFilters = false;
      this.loadTickets();
    },

    resetFormFilters() {
      this.formFilters = {
        search: '',
        status: '',
        priority: -1,
        type: '',
        userId: ''
      };
      this.itemsFilters = { ...this.formFilters };
      this.showFilters = false;
      this.loadTickets();
    },

    cancelFilters() {
      this.formFilters = { ...this.itemsFilters };
      this.showFilters = false;
    },

    resetFilterWithKey(key) {
      if (key === 'priority') {
        this.itemsFilters[key] = -1;
        this.formFilters[key] = -1;
      } else {
        this.itemsFilters[key] = '';
        this.formFilters[key] = '';
      }
    },

    openPreview(ticket) {
      this.selectedTicketId = ticket.id;
      this.showPreviewSidebar = true;
    },

    onPreviewClosed() {
      this.selectedTicketId = null;
      // Refresh list in case something changed
      //this.loadTickets();
    },

    navigateToDetails(ticketId) {
      this.$router.push({
        name: 'Support.NewTickets.View',
        params: { id: ticketId }
      });
    },

    onPriorityChanged(ticketId, priority) {
      this.$http
        .post('/api/common/tickets/changePriority/' + ticketId + '/' + priority)
        .then(resp => {
          this.loadTickets();
        })
        .catch(err => {
          console.error('Failed to change priority', err);
        });
    }
  }
};
</script>
